import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FiSave, FiUpload, FiPlus, FiTrash2, FiImage, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const PAGE_CONFIG = {
  homepage: { label: 'Homepage', icon: '🏠' },
  about: { label: 'About', icon: '📋' },
  contact: { label: 'Contact', icon: '✉️' },
  careers: { label: 'Careers', icon: '💼' },
  business_sourcing: { label: 'Sourcing', icon: '📦' },
  business_logistics: { label: 'Logistics', icon: '🚢' },
  business_exports: { label: 'Exports', icon: '🌍' },
  business_technologies: { label: 'Technologies', icon: '💻' },
  business_digital: { label: 'Digital', icon: '📊' },
  business_infra: { label: 'Infra', icon: '🏗️' },
  business_retail: { label: 'Retail', icon: '🛍️' },
};

const CmsPage = () => {
  const [activeKey, setActiveKey] = useState('homepage');
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [images, setImages] = useState([]);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [imagePickerTarget, setImagePickerTarget] = useState(null);
  const [expandedSections, setExpandedSections] = useState({ main: true, businesses: false });

  const fetchContent = useCallback(async (key) => {
    try {
      const r = await api.get(`/api/content/${key}`);
      setContent(r.data);
    } catch { setContent(null); }
  }, []);

  const fetchImages = async () => {
    try {
      const r = await api.get('/api/admin/images');
      setImages(r.data);
    } catch {}
  };

  useEffect(() => { fetchContent(activeKey); }, [activeKey, fetchContent]);
  useEffect(() => { fetchImages(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/api/admin/content/${activeKey}`, content);
      toast.success('Content saved!');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const updateField = (field, value) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  const updateNestedField = (arrayField, index, subField, value) => {
    setContent(prev => {
      const arr = [...(prev[arrayField] || [])];
      arr[index] = { ...arr[index], [subField]: value };
      return { ...prev, [arrayField]: arr };
    });
  };

  const addToArray = (field, template) => {
    setContent(prev => ({ ...prev, [field]: [...(prev[field] || []), template] }));
  };

  const removeFromArray = (field, index) => {
    setContent(prev => ({ ...prev, [field]: prev[field].filter((_, i) => i !== index) }));
  };

  const handleImageUpload = async (e, targetField) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('image', file);
    try {
      const r = await api.post('/api/admin/upload-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      updateField(targetField, r.data.image_url);
      fetchImages();
      toast.success('Image uploaded!');
    } catch { toast.error('Upload failed'); }
  };

  const openImagePicker = (targetField) => {
    setImagePickerTarget(targetField);
    setShowImagePicker(true);
    fetchImages();
  };

  const selectImage = (url) => {
    if (imagePickerTarget) updateField(imagePickerTarget, url);
    setShowImagePicker(false);
    setImagePickerTarget(null);
  };

  const inputClass = "w-full px-4 py-3 bg-white/5 border border-white/10 rounded text-white placeholder-white/25 focus:outline-none focus:border-[#17B9BE]/50 transition-colors text-sm";
  const labelClass = "block text-[10px] uppercase tracking-widest text-white/40 mb-1.5 font-semibold";

  const mainPages = ['homepage', 'about', 'contact', 'careers'];
  const businessPages = Object.keys(PAGE_CONFIG).filter(k => k.startsWith('business_'));

  if (!content) return <div className="text-white/30 py-10 text-center">Loading...</div>;

  return (
    <div data-testid="cms-page">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl font-bold">
          Page Editor — <span className="text-[#17B9BE]">{PAGE_CONFIG[activeKey]?.label}</span>
        </h2>
        <button onClick={handleSave} disabled={saving} data-testid="cms-save-button"
          className="flex items-center px-5 py-2.5 brand-gradient-bg text-black rounded-sm text-sm font-semibold hover:opacity-90 disabled:opacity-50">
          <FiSave className="mr-2" size={14} /> {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <div className="flex gap-6">
        {/* Sidebar nav */}
        <div className="w-48 flex-shrink-0 space-y-1">
          <button onClick={() => setExpandedSections(p => ({ ...p, main: !p.main }))}
            className="w-full flex items-center justify-between text-xs uppercase tracking-widest text-white/40 font-semibold py-2">
            Pages {expandedSections.main ? <FiChevronDown size={12} /> : <FiChevronRight size={12} />}
          </button>
          {expandedSections.main && mainPages.map(key => (
            <button key={key} onClick={() => setActiveKey(key)} data-testid={`cms-nav-${key}`}
              className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${activeKey === key ? 'bg-[#17B9BE]/10 text-[#17B9BE]' : 'text-white/40 hover:text-white hover:bg-white/5'}`}>
              {PAGE_CONFIG[key]?.icon} {PAGE_CONFIG[key]?.label}
            </button>
          ))}

          <button onClick={() => setExpandedSections(p => ({ ...p, businesses: !p.businesses }))}
            className="w-full flex items-center justify-between text-xs uppercase tracking-widest text-white/40 font-semibold py-2 mt-3">
            Verticals {expandedSections.businesses ? <FiChevronDown size={12} /> : <FiChevronRight size={12} />}
          </button>
          {expandedSections.businesses && businessPages.map(key => (
            <button key={key} onClick={() => setActiveKey(key)} data-testid={`cms-nav-${key}`}
              className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${activeKey === key ? 'bg-[#17B9BE]/10 text-[#17B9BE]' : 'text-white/40 hover:text-white hover:bg-white/5'}`}>
              {PAGE_CONFIG[key]?.icon} {PAGE_CONFIG[key]?.label}
            </button>
          ))}
        </div>

        {/* Editor area */}
        <div className="flex-1 space-y-5 max-h-[calc(100vh-220px)] overflow-y-auto pr-2">
          {/* HOMEPAGE */}
          {activeKey === 'homepage' && (
            <>
              <Section title="Hero Section">
                <Field label="Welcome Text" value={content.hero_welcome} onChange={v => updateField('hero_welcome', v)} />
                <Field label="Title Line 1" value={content.hero_title_line1} onChange={v => updateField('hero_title_line1', v)} />
                <Field label="Title Line 2 (Gradient)" value={content.hero_title_line2} onChange={v => updateField('hero_title_line2', v)} />
                <Field label="Description" value={content.hero_description} onChange={v => updateField('hero_description', v)} textarea />
                <ImageField label="Hero Image" value={content.hero_image} onChange={v => updateField('hero_image', v)} onUpload={e => handleImageUpload(e, 'hero_image')} onPick={() => openImagePicker('hero_image')} />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="CTA Primary Text" value={content.hero_cta_primary} onChange={v => updateField('hero_cta_primary', v)} />
                  <Field label="CTA Secondary Text" value={content.hero_cta_secondary} onChange={v => updateField('hero_cta_secondary', v)} />
                </div>
              </Section>
              <Section title="Statistics">
                {content.stats?.map((s, i) => (
                  <div key={i} className="grid grid-cols-3 gap-3 items-end">
                    <Field label={`Stat ${i+1} Value`} value={s.value} onChange={v => updateNestedField('stats', i, 'value', v)} />
                    <Field label="Label" value={s.label} onChange={v => updateNestedField('stats', i, 'label', v)} />
                    <Field label="Description" value={s.description} onChange={v => updateNestedField('stats', i, 'description', v)} />
                  </div>
                ))}
              </Section>
              <Section title="Verticals Section">
                <Field label="Section Title" value={content.verticals_title} onChange={v => updateField('verticals_title', v)} />
                <Field label="Title Highlight (Gradient)" value={content.verticals_title_highlight} onChange={v => updateField('verticals_title_highlight', v)} />
                <Field label="Subtitle" value={content.verticals_subtitle} onChange={v => updateField('verticals_subtitle', v)} textarea />
              </Section>
              <Section title="CTA Section">
                <Field label="CTA Title Line 1" value={content.cta_title_line1} onChange={v => updateField('cta_title_line1', v)} />
                <Field label="CTA Highlight" value={content.cta_title_highlight} onChange={v => updateField('cta_title_highlight', v)} />
                <Field label="CTA Description" value={content.cta_description} onChange={v => updateField('cta_description', v)} textarea />
              </Section>
            </>
          )}

          {/* ABOUT */}
          {activeKey === 'about' && (
            <>
              <Section title="Hero Section">
                <Field label="Title Line 1" value={content.hero_title_line1} onChange={v => updateField('hero_title_line1', v)} />
                <Field label="Title Highlight" value={content.hero_title_highlight} onChange={v => updateField('hero_title_highlight', v)} />
                <Field label="Description" value={content.hero_description} onChange={v => updateField('hero_description', v)} textarea />
                <ImageField label="Hero Image" value={content.hero_image} onChange={v => updateField('hero_image', v)} onUpload={e => handleImageUpload(e, 'hero_image')} onPick={() => openImagePicker('hero_image')} />
              </Section>
              <Section title="Corporate Overview">
                <Field label="Section Title" value={content.overview_title} onChange={v => updateField('overview_title', v)} />
                <Field label="Title Highlight" value={content.overview_title_highlight} onChange={v => updateField('overview_title_highlight', v)} />
                <Field label="Paragraph 1" value={content.overview_paragraph1} onChange={v => updateField('overview_paragraph1', v)} textarea />
                <Field label="Paragraph 2" value={content.overview_paragraph2} onChange={v => updateField('overview_paragraph2', v)} textarea />
                <ImageField label="Overview Image" value={content.overview_image} onChange={v => updateField('overview_image', v)} onUpload={e => handleImageUpload(e, 'overview_image')} onPick={() => openImagePicker('overview_image')} />
              </Section>
              <Section title="Global Locations">
                {content.locations?.map((loc, i) => (
                  <div key={i} className="grid grid-cols-3 gap-3 items-end">
                    <Field label="City" value={loc.city} onChange={v => updateNestedField('locations', i, 'city', v)} />
                    <Field label="Country" value={loc.country} onChange={v => updateNestedField('locations', i, 'country', v)} />
                    <div className="flex gap-2 items-end">
                      <div className="flex-1"><Field label="Role" value={loc.role} onChange={v => updateNestedField('locations', i, 'role', v)} /></div>
                      <button onClick={() => removeFromArray('locations', i)} className="p-3 text-red-400/50 hover:text-red-400"><FiTrash2 size={14} /></button>
                    </div>
                  </div>
                ))}
                <button onClick={() => addToArray('locations', { city: '', country: '', role: '' })} className="text-xs text-[#17B9BE] flex items-center gap-1 mt-2"><FiPlus size={12} /> Add Location</button>
              </Section>
              <Section title="Values">
                {content.values?.map((v, i) => (
                  <div key={i} className="space-y-2 p-3 bg-white/[0.02] rounded border border-white/5">
                    <div className="flex gap-3 items-end">
                      <div className="flex-1"><Field label={`Value ${i+1} Title`} value={v.title} onChange={val => updateNestedField('values', i, 'title', val)} /></div>
                      <button onClick={() => removeFromArray('values', i)} className="p-3 text-red-400/50 hover:text-red-400"><FiTrash2 size={14} /></button>
                    </div>
                    <Field label="Description" value={v.description} onChange={val => updateNestedField('values', i, 'description', val)} textarea />
                  </div>
                ))}
                <button onClick={() => addToArray('values', { title: '', description: '' })} className="text-xs text-[#17B9BE] flex items-center gap-1 mt-2"><FiPlus size={12} /> Add Value</button>
              </Section>
              <Section title="Leadership Message">
                <Field label="Message" value={content.leadership_message} onChange={v => updateField('leadership_message', v)} textarea />
                <Field label="Attribution" value={content.leadership_attribution} onChange={v => updateField('leadership_attribution', v)} />
                <Field label="Subtext" value={content.leadership_subtext} onChange={v => updateField('leadership_subtext', v)} />
              </Section>
            </>
          )}

          {/* CONTACT */}
          {activeKey === 'contact' && (
            <>
              <Section title="Hero Section">
                <Field label="Title Line 1" value={content.hero_title_line1} onChange={v => updateField('hero_title_line1', v)} />
                <Field label="Title Highlight" value={content.hero_title_highlight} onChange={v => updateField('hero_title_highlight', v)} />
                <Field label="Description" value={content.hero_description} onChange={v => updateField('hero_description', v)} textarea />
              </Section>
              <Section title="Contact Details">
                <Field label="Address" value={content.address} onChange={v => updateField('address', v)} textarea />
                <Field label="Email" value={content.email} onChange={v => updateField('email', v)} />
                <Field label="Phone" value={content.phone} onChange={v => updateField('phone', v)} />
                <Field label="Business Hours" value={content.business_hours} onChange={v => updateField('business_hours', v)} textarea />
              </Section>
            </>
          )}

          {/* CAREERS */}
          {activeKey === 'careers' && (
            <>
              <Section title="Hero Section">
                <Field label="Title Line 1" value={content.hero_title_line1} onChange={v => updateField('hero_title_line1', v)} />
                <Field label="Title Highlight" value={content.hero_title_highlight} onChange={v => updateField('hero_title_highlight', v)} />
                <Field label="Description" value={content.hero_description} onChange={v => updateField('hero_description', v)} textarea />
              </Section>
              <Section title="Perks">
                {content.perks?.map((p, i) => (
                  <div key={i} className="grid grid-cols-4 gap-3 items-end">
                    <Field label="Number" value={p.num} onChange={v => updateNestedField('perks', i, 'num', v)} />
                    <Field label="Title" value={p.title} onChange={v => updateNestedField('perks', i, 'title', v)} />
                    <div className="col-span-2 flex gap-2 items-end">
                      <div className="flex-1"><Field label="Description" value={p.description} onChange={v => updateNestedField('perks', i, 'description', v)} /></div>
                      <button onClick={() => removeFromArray('perks', i)} className="p-3 text-red-400/50 hover:text-red-400"><FiTrash2 size={14} /></button>
                    </div>
                  </div>
                ))}
                <button onClick={() => addToArray('perks', { num: '', title: '', description: '' })} className="text-xs text-[#17B9BE] flex items-center gap-1 mt-2"><FiPlus size={12} /> Add Perk</button>
              </Section>
            </>
          )}

          {/* BUSINESS VERTICALS */}
          {activeKey.startsWith('business_') && (
            <>
              <Section title="Vertical Info">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Title" value={content.title} onChange={v => updateField('title', v)} />
                  <Field label="Subtitle" value={content.subtitle} onChange={v => updateField('subtitle', v)} />
                </div>
                <Field label="Short Description" value={content.description} onChange={v => updateField('description', v)} textarea />
                <ImageField label="Cover Image" value={content.image} onChange={v => updateField('image', v)} onUpload={e => handleImageUpload(e, 'image')} onPick={() => openImagePicker('image')} />
              </Section>
              <Section title="Full Description">
                <Field label="Full Description" value={content.full_description} onChange={v => updateField('full_description', v)} textarea />
              </Section>
              <Section title="Key Services">
                {content.services?.map((s, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <input value={s} onChange={e => {
                      const arr = [...content.services];
                      arr[i] = e.target.value;
                      updateField('services', arr);
                    }} className={`${inputClass} flex-1`} />
                    <button onClick={() => updateField('services', content.services.filter((_, idx) => idx !== i))} className="p-2 text-red-400/50 hover:text-red-400"><FiTrash2 size={14} /></button>
                  </div>
                ))}
                <button onClick={() => updateField('services', [...(content.services || []), ''])} className="text-xs text-[#17B9BE] flex items-center gap-1 mt-2"><FiPlus size={12} /> Add Service</button>
              </Section>
            </>
          )}
        </div>
      </div>

      {/* Image Picker Modal */}
      {showImagePicker && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-[#111] rounded border border-white/10 w-full max-w-3xl max-h-[80vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-display text-lg font-bold">Select Image</h3>
              <button onClick={() => setShowImagePicker(false)} className="text-white/30 hover:text-white text-sm">Close</button>
            </div>
            {images.length === 0 ? (
              <p className="text-white/30 text-sm">No uploaded images. Upload one using the Upload button on the field.</p>
            ) : (
              <div className="grid grid-cols-4 gap-3">
                {images.map(img => (
                  <button key={img.id} onClick={() => selectImage(`/api/images/${img.id}`)}
                    className="relative aspect-video rounded overflow-hidden border border-white/10 hover:border-[#17B9BE] transition-colors">
                    <img src={`${BACKEND_URL}/api/images/${img.id}`} alt={img.filename} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Reusable components
const Section = ({ title, children }) => (
  <div className="bg-[#0a0a0a] rounded border border-white/5 p-5">
    <h3 className="text-xs uppercase tracking-widest text-[#17B9BE] font-semibold mb-4">{title}</h3>
    <div className="space-y-3">{children}</div>
  </div>
);

const Field = ({ label, value, onChange, textarea }) => {
  const cls = "w-full px-4 py-3 bg-white/5 border border-white/10 rounded text-white placeholder-white/25 focus:outline-none focus:border-[#17B9BE]/50 transition-colors text-sm";
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest text-white/40 mb-1.5 font-semibold">{label}</label>
      {textarea ? (
        <textarea value={value || ''} onChange={e => onChange(e.target.value)} className={cls} rows="3" />
      ) : (
        <input value={value || ''} onChange={e => onChange(e.target.value)} className={cls} />
      )}
    </div>
  );
};

const ImageField = ({ label, value, onChange, onUpload, onPick }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-widest text-white/40 mb-1.5 font-semibold">{label}</label>
    <div className="flex gap-2">
      <input value={value || ''} onChange={e => onChange(e.target.value)}
        className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded text-white text-sm focus:outline-none focus:border-[#17B9BE]/50" placeholder="Image URL" />
      <label className="flex-shrink-0 px-3 py-3 rounded bg-[#17B9BE]/20 text-[#17B9BE] cursor-pointer text-sm flex items-center gap-1.5 hover:bg-[#17B9BE]/30">
        <FiUpload size={14} /> Upload
        <input type="file" accept="image/*" onChange={onUpload} className="hidden" />
      </label>
      <button onClick={onPick} className="flex-shrink-0 px-3 py-3 rounded bg-white/5 text-white/40 hover:text-white text-sm flex items-center gap-1.5 hover:bg-white/10">
        <FiImage size={14} /> Gallery
      </button>
    </div>
    {value && (
      <div className="mt-2 h-24 rounded overflow-hidden border border-white/10">
        <img src={value.startsWith('/api') ? `${BACKEND_URL}${value}` : value} alt="Preview" className="w-full h-full object-cover" />
      </div>
    )}
  </div>
);

export default CmsPage;
